import React, { useEffect, useState } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';
import { 
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState(new Set());

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'FAQ' }
  ];

  const faqCategories = [
    {
      title: 'Account & Registration',
      items: [
        {
          question: 'How do I create an account?',
          answer: 'Click the "Register" button in the top navigation, fill in your details including a valid email address, and verify your email. Your email will be your username for logging in.'
        },
        {
          question: 'I forgot my password. How do I reset it?',
          answer: 'Click "Forgot Password" on the login page, enter your email address, and follow the instructions sent to your email to reset your password.'
        },
        {
          question: 'Can I change my email address?',
          answer: 'Currently, email addresses cannot be changed after registration. If you need to use a different email, please contact support at support@gcra.gov.za.'
        },
        {
          question: 'Why do I need to verify my email?',
          answer: 'Email verification ensures we can send you important course updates, application status notifications, and other critical information about your training programs.'
        }
      ]
    },
    {
      title: 'Course Applications',
      items: [
        {
          question: 'How do I apply for a course?',
          answer: 'Browse courses, click on a course you\'re interested in, review the requirements, and click "Apply Now". Make sure your profile is complete before applying.'
        },
        {
          question: 'What documents do I need to apply?',
          answer: 'Typically you need: ID document, matric certificate or highest qualification, proof of address, and any specific documents mentioned in the course requirements.'
        },
        {
          question: 'How long does the application process take?',
          answer: 'Application review typically takes 5-10 business days. You\'ll receive email notifications about status changes and can track progress in your dashboard.'
        },
        {
          question: 'Can I apply for multiple courses?',
          answer: 'Yes, you can apply for multiple courses, but ensure you can commit to the training schedule if accepted for multiple programs.'
        },
        {
          question: 'What if my application is rejected?',
          answer: 'You\'ll receive feedback on why your application wasn\'t successful. You can reapply for future intakes or apply for other suitable courses.'
        }
      ]
    },
    {
      title: 'Opportunities & Jobs',
      items: [
        {
          question: 'What types of opportunities are available?',
          answer: 'The platform offers job opportunities, apprenticeships, and internships across various industries in Gauteng Province. These range from entry-level to experienced positions.'
        },
        {
          question: 'How do I apply for job opportunities?',
          answer: 'Browse opportunities, review the requirements, and click "Apply Now". Ensure your profile is complete and matches the job requirements for the best chance of success.'
        },
        {
          question: 'Can I save opportunities for later?',
          answer: 'Yes! Click the heart icon on any opportunity card to add it to your wishlist. You can access your saved opportunities from your dashboard.'
        },
        {
          question: 'How do I track my opportunity applications?',
          answer: 'All your applications are tracked in your dashboard. You\'ll receive email notifications about status changes and can see the current status of each application.'
        },
        {
          question: 'What\'s the difference between jobs, apprenticeships, and internships?',
          answer: 'Jobs are permanent or contract positions. Apprenticeships combine work with structured training and mentorship. Internships are shorter-term placements focused on gaining experience.'
        }
      ]
    },
    {
      title: 'Career Guidance',
      items: [
        {
          question: 'What is the Career Guidance system?',
          answer: 'Career Guidance provides personalized course and opportunity recommendations based on your skills, interests, and career goals through a comprehensive assessment.'
        },
        {
          question: 'How long does the career assessment take?',
          answer: 'The assessment typically takes 15-20 minutes to complete. It covers your skills, interests, and preferences to generate accurate recommendations.'
        },
        {
          question: 'Can I retake the career assessment?',
          answer: 'Yes, you can retake the assessment anytime. This is recommended when your skills, interests, or goals change, or when you want updated recommendations.'
        },
        {
          question: 'How accurate are the recommendations?',
          answer: 'Recommendations are based on advanced matching algorithms and your assessment responses. The more honest and detailed your responses, the more accurate the recommendations.'
        },
        {
          question: 'What do the match scores mean?',
          answer: 'Match scores (60-100%) indicate how well a course or opportunity aligns with your profile. Higher scores mean better alignment with your skills and interests.'
        }
      ]
    },
    {
      title: 'Profile & Personal Information',
      items: [
        {
          question: 'Why is my profile completion important?',
          answer: 'A complete profile helps us match you with suitable courses, verify your eligibility, and increases your chances of application acceptance.'
        },
        {
          question: 'How do I update my educational background?',
          answer: 'Go to your Profile page, scroll to the Education section, and add your matric results and any tertiary qualifications. You can add multiple qualifications.'
        },
        {
          question: 'What is the economic corridor allocation?',
          answer: 'Based on your postal code, you\'re allocated to one of Gauteng\'s 5 economic corridors (North, South, East, West, Central) for training program planning.'
        },
        {
          question: 'Can I change my location details?',
          answer: 'Yes, you can update your address and postal code in your profile. This may affect your economic corridor allocation and course recommendations.'
        }
      ]
    },
    {
      title: 'Technical Issues',
      items: [
        {
          question: 'The website is not loading properly. What should I do?',
          answer: 'Try refreshing the page, clearing your browser cache, or using a different browser. If issues persist, contact support with details about your browser and device.'
        },
        {
          question: 'I can\'t upload my documents. What\'s wrong?',
          answer: 'Ensure your files are in PDF or image format (JPG, PNG) and under 5MB each. Check your internet connection and try again.'
        },
        {
          question: 'Why am I not receiving email notifications?',
          answer: 'Check your spam/junk folder, ensure your email address is correct in your profile, and add support@gcra.gov.za to your contacts.'
        },
        {
          question: 'The application form won\'t submit. What should I do?',
          answer: 'Ensure all required fields are completed, check that uploaded documents are valid, and try submitting again. If it still fails, contact support.'
        }
      ]
    },
    {
      title: 'Course Information',
      items: [
        {
          question: 'How do I find courses suitable for me?',
          answer: 'Use the search and filter options on the Courses page. Filter by location, duration, NQF level, or search for specific skills or industries.'
        },
        {
          question: 'What do the NQF levels mean?',
          answer: 'NQF (National Qualifications Framework) levels indicate the complexity: Level 1-4 are basic to matric equivalent, Level 5-6 are post-matric certificates/diplomas, Level 7+ are degree level.'
        },
        {
          question: 'Are the courses free?',
          answer: 'Most GCRA training programs are free for qualifying South African citizens. Some specialized courses may have fees, which will be clearly indicated.'
        },
        {
          question: 'How long are the training programs?',
          answer: 'Course duration varies from short skills programs (few weeks) to comprehensive qualifications (6-24 months). Check individual course details for specific durations.'
        }
      ]
    }
  ];

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(key)) {
      newOpenItems.delete(key);
    } else {
      newOpenItems.add(key);
    }
    setOpenItems(newOpenItems);
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    items: category.items.filter(item =>
      searchQuery === '' ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.items.length > 0);

  return (
    <HelpLayout title="Frequently Asked Questions" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        {/* Introduction */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Frequently Asked Questions
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              Find quick answers to common questions about using GMSP3 Career Guidance.
              If you can't find what you're looking for, contact our support team.
            </p>

            {/* Search */}
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <input
                type="text"
                placeholder="Search FAQ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  border: '1px solid var(--border-gray)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'var(--white)',
                  outline: 'none'
                }}
              />
              <MagnifyingGlassIcon style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: 'var(--text-gray)'
              }} />
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        {filteredCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="card" style={{ marginBottom: '24px' }}>
            <div className="card-body">
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '2px solid var(--primary-yellow)'
              }}>
                {category.title}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {category.items.map((item, itemIndex) => {
                  const key = `${categoryIndex}-${itemIndex}`;
                  const isOpen = openItems.has(key);
                  
                  return (
                    <div key={itemIndex} style={{
                      border: '1px solid var(--border-gray)',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <button
                        onClick={() => toggleItem(categoryIndex, itemIndex)}
                        style={{
                          width: '100%',
                          padding: '16px',
                          backgroundColor: isOpen ? 'var(--primary-yellow-light)' : 'var(--background-gray)',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'var(--primary-black)',
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <span>{item.question}</span>
                        {isOpen ? (
                          <ChevronUpIcon style={{ width: '20px', height: '20px', color: 'var(--primary-yellow)' }} />
                        ) : (
                          <ChevronDownIcon style={{ width: '20px', height: '20px', color: 'var(--text-gray)' }} />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div style={{
                          padding: '16px',
                          backgroundColor: 'var(--white)',
                          borderTop: '1px solid var(--border-gray)'
                        }}>
                          <p style={{
                            fontSize: '14px',
                            color: 'var(--text-gray)',
                            lineHeight: 1.6,
                            margin: 0
                          }}>
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {/* No Results */}
        {searchQuery && filteredCategories.length === 0 && (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                No results found
              </h3>
              <p style={{
                fontSize: '16px',
                color: 'var(--text-gray)',
                marginBottom: '24px'
              }}>
                We couldn't find any FAQ items matching "{searchQuery}". Try different keywords or contact support.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="btn btn-primary"
              >
                Clear Search
              </button>
            </div>
          </div>
        )}

        {/* Contact Support */}
        <div className="card">
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Still Need Help?
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              marginBottom: '24px'
            }}>
              If you couldn't find the answer to your question, our support team is here to help.
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <a href="mailto:support@gcra.gov.za" className="btn btn-primary">
                Email Support
              </a>
              <a href="tel:+27123456789" className="btn btn-outline">
                Call Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default FAQ;
